<template>
  <q-item
    @mouseover="hovering = true"
    @mouseleave="hovering = false"
    clickable
    dense
    :label="label"
    :class="['my-gradient-button', btnClass]"
    :style="btnStyle"
    v-bind="$attrs"
    :round="round"
    :flat="true"
    :rounded="rounded"
  >
    <div v-if="loading" class="row flex-center">
      <q-spinner-infinity/>
      <div class="q-ml-sm">just a moment</div>
    </div>

    <div v-else class="row no-wrap items-center">
      <q-icon name="mdi-creation" />
      <div v-if="label" class="q-ml-sm">{{ label }}</div>
      <div class="q-ml-sm"><slot /></div>
    </div>
  </q-item>
</template>

<script>
export default {
  data() {
    return {
      hovering: false,
      // Store current rotation in degrees (number)
      currentRotation: 82,
      animating: false,
      requestId: null,
      lastTime: null,
    }
  },
  props: {
    label: {
      type: String,
      default: '',
    },
    // When true, the button will have a square border (0px radius)
    square: {
      type: Boolean,
      default: false,
    },
    // When true (and square is false), the button will have a rounded border (30px radius)
    round: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    flat: {
      type: Boolean,
      default: false,
    },
    // Animate gradient only on hover when true
    animOnHover: {
      type: Boolean,
      default: true,
    },
    // Animate gradient continuously when true
    animAlways: {
      type: Boolean,
      default: false,
    },
    // Inner fill color for the button
    color: {
      type: String,
      default: 'rgb(18, 18, 18)',
    },
    // Text color for the button
    textColor: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // Use dark mode if available.
    bgColor() {
      if (this.$q.dark.isActive) return '#253346'
      // Otherwise default to white.
      return '#ffffff'
    },
    // Return the rotation as a CSS angle string.
    rotationString() {
      return this.currentRotation + 'deg'
    },
    btnStyle() {
      const style = ['font-size: var(--ec-type-body-lg-size);']

      // Determine border radius based on props.
      if (this.square) {
        style.push('border-radius: 0px;')
      } else if (this.rounded || this.round) {
        style.push('border-radius: 30px;')
      } else {
        style.push('border-radius: 100px;')
      }

      // Set the two-layer background:
      // • The first layer uses bgColor to fill the inner area.
      // • The second layer is your fixed gradient using the dynamic rotation.
      style.push(
        `background-image: linear-gradient(${this.bgColor}, ${this.bgColor}), ` +
          `linear-gradient(${this.rotationString}, rgb(91, 140, 255) 11.87%, ` +
          `rgb(164, 202, 251) 38.42%, rgb(167, 237, 255) 64.19%, ` +
          `rgb(253, 173, 238) 89.96%);`
      )

      // (No CSS animation is applied here—animation is driven via JS.)

      return style
    },
    btnClass() {
      const _class = ['flex-center']
      if (!this.textColor) _class.push('my-text1')
      return _class
    },
    // Determine if we should animate (either always or on hover)
    shouldAnimate() {
      return this.animAlways || (this.animOnHover && this.hovering) || this.loading
    },
  },
  watch: {
    shouldAnimate(newVal) {
      if (newVal) {
        this.startAnimation()
      } else {
        this.stopAnimation()
      }
    },
  },
  methods: {
    startAnimation() {
      if (this.animating) return
      this.animating = true
      this.lastTime = null
      const animate = (timestamp) => {
        if (!this.animating) return
        if (!this.lastTime) this.lastTime = timestamp
        const delta = timestamp - this.lastTime
        this.lastTime = timestamp
        // Calculate rotation increment. For a full rotation (360deg) in 3000ms:
        const speed = 360 / 3000 // degrees per millisecond
        this.currentRotation = (this.currentRotation + delta * speed) % 360
        this.requestId = requestAnimationFrame(animate)
      }
      this.requestId = requestAnimationFrame(animate)
    },
    stopAnimation() {
      this.animating = false
      if (this.requestId) {
        cancelAnimationFrame(this.requestId)
        this.requestId = null
      }
    },
  },
  beforeUnmount() {
    this.stopAnimation()
  },
}
</script>

<style scoped>
.my-gradient-button {
  border: 2px solid transparent; /* Maintain border thickness */
  background-origin: border-box;
  background-clip: padding-box, border-box;
  transition: all 0.3s ease;
  box-shadow: rgba(45, 35, 66, 0.35) 0 1px 2px;
}
</style>
